import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { borderRadius, getModernButton, getModernScrollbar, layoutGap } from '../../../theme/theme';
import type { ValidationDefinition } from '../types';
import { compareValidationDefinitionsByCriticality } from '../validationConstants';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import HelpTooltipTrigger from '../../../shared/ui/common/HelpTooltipTrigger';
import { validationActionTooltips } from '../../../shared/config/actionHelpCatalog';
import WorkspaceSpotlightTour from '../../../shared/ui/common/WorkspaceSpotlightTour';
import ValidationConfigurationGuideDialog from './ValidationConfigurationGuideDialog';
import ValidationPriorityBadge from './ValidationPriorityBadge';
import { buildValidationConfigurationTourSteps } from './validationConfigurationGuideContent';
import {
  setDurableStateValue,
  VALIDATION_CONFIGURATION_ONBOARDING_STORAGE_KEY,
} from '../../../shared/lib/durableState/durableState';
// Writes go through the durable mirror so the "I've already seen the tour"
// flag survives browser restart / tab close and WebView2 cache purges;
// session-scoped or local-only storage re-popped the auto-tour every fresh
// session. Reads hit localStorage, restored from the plugin mirror at
// bootstrap.

const VALIDATION_CONFIGURATION_ONBOARDING_KEY = VALIDATION_CONFIGURATION_ONBOARDING_STORAGE_KEY;

const hasSeenValidationConfigurationOnboarding = () => {
  try {
    return window.localStorage.getItem(VALIDATION_CONFIGURATION_ONBOARDING_KEY) === '1';
  } catch {
    return false;
  }
};

const markValidationConfigurationOnboardingSeen = () => {
  try {
    setDurableStateValue(VALIDATION_CONFIGURATION_ONBOARDING_KEY, '1');
  } catch {
    // Ignore storage failures; onboarding can reopen next visit.
  }
};

interface ConfigurationDialogProps {
  open: boolean;
  onClose: () => void;
  validations: ValidationDefinition[];
  selectedValidations: number[];
  onToggleValidation: (id: number) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export default function ConfigurationDialog({
  open,
  onClose,
  validations,
  selectedValidations,
  onToggleValidation,
  onSelectAll,
  onDeselectAll,
}: ConfigurationDialogProps) {
  const theme = useTheme();
  const buttonStyles = useMemo(() => getModernButton(theme), [theme]);
  const [guideOpen, setGuideOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const contextRef = useRef<HTMLDivElement | null>(null);
  const bulkActionsRef = useRef<HTMLDivElement | null>(null);
  const firstCardRef = useRef<HTMLDivElement | null>(null);
  const firstToggleRef = useRef<HTMLDivElement | null>(null);
  const closeActionRef = useRef<HTMLButtonElement | null>(null);
  const sortedValidations = useMemo(
    () => [...validations].sort(compareValidationDefinitionsByCriticality),
    [validations]
  );
  const firstValidationId = sortedValidations[0]?.Id ?? null;
  const tourSteps = useMemo(
    () =>
      buildValidationConfigurationTourSteps({
        refs: {
          contextRef,
          bulkActionsRef,
          firstCardRef,
          firstToggleRef,
          closeActionRef,
        },
      }),
    []
  );

  const handleCloseTour = useCallback(() => {
    setTourOpen(false);
    setTourStep(0);
  }, []);

  const openConfigurationTour = useCallback((rememberOnboarding: boolean) => {
    if (!open || sortedValidations.length === 0) {
      return;
    }

    setGuideOpen(false);
    setTourStep(0);
    setTourOpen(true);
    if (rememberOnboarding && !hasSeenValidationConfigurationOnboarding()) {
      markValidationConfigurationOnboardingSeen();
    }
  }, [open, sortedValidations.length]);

  const handleOpenGuide = useCallback(() => {
    handleCloseTour();
    setGuideOpen(true);
  }, [handleCloseTour]);

  const handleOpenTour = useCallback(() => {
    openConfigurationTour(true);
  }, [openConfigurationTour]);

  useEffect(() => {
    if (!open) {
      setGuideOpen(false);
      handleCloseTour();
      return;
    }

    if (sortedValidations.length === 0 || hasSeenValidationConfigurationOnboarding()) {
      return;
    }

    openConfigurationTour(true);
  }, [handleCloseTour, open, openConfigurationTour, sortedValidations.length]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: `${borderRadius.lg}px`,
            border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.34 : 0.22)}`,
          },
        }}
      >
      <DialogTitle sx={{ pb: 1.25 }}>
        <Box ref={contextRef} data-testid="validation-config-tour-target-context">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.row, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 750, minWidth: 0 }}>
                Configure validation checks
              </Typography>
              <HelpTooltipTrigger
        label="Configure validation checks"
                help={validationActionTooltips.configureChecks}
                placement="bottom-start"
              />
            </Box>
            <Button size="small" variant="text" onClick={handleOpenGuide}>
              Guide
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Select which validation checks to run
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          maxHeight: '60vh',
          overflowY: 'auto',
          backgroundColor: alpha(theme.palette.background.default, theme.palette.mode === 'light' ? 0.28 : 0.12),
          ...getModernScrollbar(theme),
        }}
      >
        <Stack spacing={layoutGap.row} mt={1}>
          <Box
            ref={bulkActionsRef}
            data-testid="validation-config-tour-target-bulk-actions"
            sx={{ mb: 2, py: 0.35 }}
          >
            <Stack direction="row" spacing={layoutGap.row} flexWrap="wrap" useFlexGap>
              <ActionHelpTooltip help={validationActionTooltips.selectAllChecks}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onSelectAll}
                  sx={{
                    borderRadius: `${borderRadius.md}px`,
                  }}
                >
                  Select all
                </Button>
              </ActionHelpTooltip>
              <ActionHelpTooltip help={validationActionTooltips.deselectAllChecks}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onDeselectAll}
                  sx={{
                    borderRadius: `${borderRadius.md}px`,
                  }}
                >
                  Deselect all
                </Button>
              </ActionHelpTooltip>
            </Stack>
          </Box>

          {sortedValidations
            .map((validation, index) => {
              const selected = selectedValidations.includes(validation.Id);

              return (
              <Card
                key={validation.Id ?? `validation-${index}`}
                variant="outlined"
                ref={validation.Id === firstValidationId ? firstCardRef : undefined}
                data-testid={
                  validation.Id === firstValidationId
                    ? 'validation-config-tour-target-first-card'
                    : undefined
                }
                sx={{
                  borderRadius: `${borderRadius.sm}px`,
                  borderColor: selected
                    ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.26 : 0.34)
                    : alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.32 : 0.2),
                  backgroundColor: selected
                    ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.035 : 0.07)
                    : alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.82 : 0.26),
                }}
              >
                <CardContent sx={{ p: 1.1, '&:last-child': { pb: 1.1 } }}>
                  <Box
                    ref={validation.Id === firstValidationId ? firstToggleRef : undefined}
                    data-testid={
                      validation.Id === firstValidationId
                        ? 'validation-config-tour-target-first-toggle'
                        : undefined
                    }
                  >
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                    control={
                      <Checkbox
                        checked={selected}
                        onChange={() => onToggleValidation(validation.Id)}
                        slotProps={{
                          input: {
                            'aria-label': `Toggle validation check #${validation.Id}: ${validation.Title}`,
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ minWidth: 0, pt: 0.2 }}>
                        <Stack direction="row" spacing={layoutGap.row} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3, minWidth: 0 }}>
                            #{validation.Id}: {validation.Title}
                          </Typography>
                          <ValidationPriorityBadge validationIds={validation.Id} compact />
                          <Chip label={validation.Category} size="small" variant="outlined" />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, lineHeight: 1.45 }}>
                          {validation.Description}
                        </Typography>
                      </Box>
                    }
                  />
                  </Box>
                </CardContent>
              </Card>
              );
            })}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          borderTop: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.3 : 0.18)}`,
          backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.88 : 0.34),
        }}
      >
        <Button
          ref={closeActionRef}
          data-testid="validation-config-tour-target-close-action"
          onClick={onClose}
          variant="outlined"
          sx={{
            ...buttonStyles.outlined,
          }}
        >
          Close
        </Button>
      </DialogActions>
      </Dialog>

      <ValidationConfigurationGuideDialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        onStartTour={handleOpenTour}
        selectedValidationCount={selectedValidations.length}
        totalValidationCount={sortedValidations.length}
      />
      <WorkspaceSpotlightTour
        open={tourOpen}
        steps={tourSteps}
        stepIndex={tourStep}
        onBack={() => setTourStep((current) => Math.max(0, current - 1))}
        onNext={() => setTourStep((current) => Math.min(tourSteps.length - 1, current + 1))}
        onJumpToStep={setTourStep}
        onClose={handleCloseTour}
        dialogLabel="Validation configuration tour"
        showArrows
      />
    </>
  );
}
